import {
  useMemo,
  useState,
  useEffect,
  RefObject,
  MutableRefObject,
} from 'react';

import { Placement } from '@popperjs/core';

import { useOnClickOutside } from '@adsk/alloy-react-helpers';

import { PLACEMENTS } from '../consts';
import usePopper from './usePopper';
import useVisibility, { UseVisibilityProps } from './useVisibility';

export type UseOverlayProps<TTarget extends HTMLElement> =
  UseVisibilityProps & {
    /** overlay position according to Overlay placements. */
    placement?: Placement;
    /** target element ref (get/set).  */
    target?: RefObject<TTarget>;
    /** overlay ref to overlay dom element. */
    overlayRef?: MutableRefObject<HTMLElement | null>;
    /** Specify whether the overlay should trigger `onHide` when the user clicks outside the overlay. */
    rootClose?: boolean;
    offset?: number[];
    /** Dependency to pass to usePopper's useLayoutEffect hook */
    layoutDependency?: unknown;
  };

function useOverlay<TTarget extends HTMLElement>({
  show,
  delayShow,
  delayHide,
  onHide,
  onShow,
  placement,
  target,
  overlayRef,
  rootClose = true,
  offset,
  layoutDependency,
}: UseOverlayProps<TTarget>) {
  const toggle = useVisibility({
    show,
    onHide,
    onShow,
    delayShow,
    delayHide,
  });

  const [overlayElement, setPopperElement] = useState<HTMLElement | null>();
  const [arrowElement, setArrowElement] = useState<HTMLElement | null>();

  useEffect(() => {
    if (overlayElement && overlayRef) {
      overlayRef.current = overlayElement;
    }
  }, [overlayElement, overlayRef]);

  const popper = usePopper({
    placement,
    show: toggle.show,
    targetElement: target?.current,
    overlayElement: overlayElement || undefined,
    arrowElement: arrowElement || undefined,
    offset,
    layoutDependency,
  });

  // Handle close on click out side
  const enableCloseOnClickOutside = rootClose && toggle.show;
  const refsToIgnore = useMemo(
    () =>
      target
        ? [{ current: overlayElement } as RefObject<HTMLElement>, target]
        : [{ current: overlayElement } as RefObject<HTMLElement>],
    [overlayElement, target]
  );
  useOnClickOutside(enableCloseOnClickOutside, toggle.handleHide, refsToIgnore);

  const targetProps = useMemo(
    () => ({
      ref: target,
    }),
    [target]
  );

  const overlayProps = useMemo(
    () => ({
      ref: setPopperElement,
      style: popper.styles.overlay,
    }),
    [popper.styles.overlay]
  );

  const arrowProps = useMemo(
    () => ({
      ref: setArrowElement,
      style: popper.styles.arrow,
    }),
    [popper.styles.arrow]
  );

  return {
    ...toggle,
    placement: popper.placement,
    targetProps,
    overlayProps,
    arrowProps,
  };
}

useOverlay.PLACEMENTS = PLACEMENTS;

export default useOverlay;
